import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import { selectChat, setOnSelect, setSelectedUser } from '../redux/reducers/messenger/chatSlice'
import { setMobileMenu } from '../redux/reducers/messenger/messengerSlice'
import useMediaQuery from '../utils/useMediaQuery'
import { getShadowRoot } from '../Webcomponent/utils/common'

/**
 * Opens a specific conversation when the host asks for one via `selectChatId`.
 *
 * This is what makes "tap an FCM notification -> land inside that chat" work:
 * the push carries `link=/messages/?chat=<id>&type=<direct|group>`, the host page
 * turns those params into the `select-chat-id` / `select-chat-type` attributes,
 * and this hook performs the same selection a click on the chat row would.
 *
 * The chat is resolved from the loaded recent chats so it can be selected with
 * its full contact object (the conversation needs more than an id). Recent chats
 * arrive asynchronously, so this re-runs until they land.
 *
 * `openedRef` makes sure a given id is auto-opened only ONCE — otherwise the
 * effect would keep yanking the user back to that chat every time recentChats
 * refetched (e.g. on each new message), making it impossible to navigate away.
 */
const useDeepLinkChat = (selectChatId?: string | null, selectChatType?: string | null) => {
  const dispatch = useAppDispatch()
  const { recentChats } = useAppSelector((state) => state.chat)
  const isMobile = useMediaQuery('(max-width: 800px)')
  const openedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!selectChatId) return
    // Already handled this deep link — let the user navigate freely.
    if (openedRef.current === selectChatId) return
    // Recent chats not loaded yet; try again once they are.
    if (!recentChats?.length) return

    const chat = recentChats.find(
      (item) =>
        String(item.chat_id) === String(selectChatId) &&
        // `type` disambiguates a group id from a user id; only enforce it when
        // the host actually provided one.
        (!selectChatType || item.chat_type === selectChatType),
    )

    if (!chat) return

    openedRef.current = selectChatId

    dispatch(selectChat({ id: chat.chat_id, type: chat.chat_type, pinned: chat.isPinned }))
    dispatch(setSelectedUser(chat))
    dispatch(setOnSelect(true))

    // On mobile the conversation lives behind the chat list, so reveal it the
    // same way selecting a row does.
    if (isMobile) {
      dispatch(setMobileMenu(true))
      getShadowRoot()?.querySelector('.sidebar-toggle')?.classList.add('mobile-menu')
    }
  }, [selectChatId, selectChatType, recentChats, isMobile, dispatch])
}

export default useDeepLinkChat
